import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');

    // Wire up Load More button if pagination type is 'load_more'
    if (this.getAttribute('pagination-type') === 'load_more') {
      this.#bindLoadMoreButton();
    }
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.#loadMoreBtn?.removeEventListener('click', this.#handleLoadMoreClick);
  }

  /** @type {HTMLButtonElement | null} */
  #loadMoreBtn = null;

  /** @type {number} */
  #loadMoreCurrentPage = 1;

  /**
   * Binds the load more button click event.
   */
  #bindLoadMoreButton() {
    const btn = this.querySelector('[ref="loadMoreBtn"]');
    if (!(btn instanceof HTMLButtonElement)) return;
    this.#loadMoreBtn = btn;
    this.#loadMoreCurrentPage = Number(btn.dataset.currentPage) || 1;
    btn.addEventListener('click', this.#handleLoadMoreClick);
  }

  /**
   * Handles load more button click — fetches next page and appends products.
   */
  #handleLoadMoreClick = async () => {
    const btn = this.#loadMoreBtn;
    if (!btn) return;

    const lastPage = Number(btn.dataset.lastPage) || 1;
    const nextPage = this.#loadMoreCurrentPage + 1;

    if (nextPage > lastPage) return;

    // Show loading state
    btn.setAttribute('data-loading', '');

    try {
      const url = new URL(window.location.href);
      url.searchParams.set('page', nextPage.toString());
      url.hash = '';

      const sectionId = this.getAttribute('section-id');
      if (!sectionId) return;

      const pageHTML = await sectionRenderer.getSectionHTML(sectionId, true, url);
      const parsed = new DOMParser().parseFromString(pageHTML, 'text/html');
      const gridEl = parsed.querySelector('[ref="grid"]');
      const newItems = gridEl?.querySelectorAll(':scope > [ref="cards[]"]');

      if (newItems && newItems.length) {
        const { grid } = this.refs;
        grid?.append(...newItems);
        this.#loadMoreCurrentPage = nextPage;

        // Appended slideshow-component elements bind their own scrollbar on connect
        // (assets/slideshow.js).

        // Update button label counter
        const label = btn.querySelector('[ref="loadMoreLabel"]');
        if (label) {
          label.innerHTML = `LOAD MORE &ndash; ${nextPage} / ${lastPage}`;
        }

        // Hide button if we've reached the last page
        if (nextPage >= lastPage) {
          btn.closest('[ref="loadMoreWrapper"]')?.remove();
        }
      }
    } catch (err) {
      console.error('[ResultsList] Load More failed:', err);
    } finally {
      btn.removeAttribute('data-loading');
    }
  };

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}

// import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
// import PaginatedList from '@theme/paginated-list';

// /**
//  * A custom element that renders a pagniated results list
//  */
// export default class ResultsList extends PaginatedList {
//   connectedCallback() {
//     super.connectedCallback();

//     mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
//     this.setAttribute('initialized', '');
//   }

//   disconnectedCallback() {
//     mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
//   }

//   /**
//    * Updates the layout.
//    *
//    * @param {Event} event
//    */
//   updateLayout({ target }) {
//     if (!(target instanceof HTMLInputElement)) return;

//     this.#animateLayoutChange(target.value);
//   }

//   /**
//    * Sets the layout.
//    *
//    * @param {string} value
//    */
//   #animateLayoutChange = async (value) => {
//     const { grid } = this.refs;

//     if (!grid) return;

//     await startViewTransition(() => this.#setLayout(value), ['product-grid']);

//     requestIdleCallback(() => {
//       const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
//       sessionStorage.setItem(`product-grid-view-${viewport}`, value);
//     });
//   };

//   /**
//    * Animates the layout change.
//    *
//    * @param {string} value
//    */
//   #setLayout(value) {
//     const { grid } = this.refs;
//     if (!grid) return;
//     grid.setAttribute('product-grid-view', value);
//   }

//   /**
//    * Handles the media query change event.
//    *
//    * @param {MediaQueryListEvent} event
//    */
//   #handleMediaQueryChange = (event) => {
//     const targetElement = event.matches
//       ? this.querySelector('[data-grid-layout="desktop-default-option"]')
//       : this.querySelector('[data-grid-layout="mobile-option"]');

//     if (!(targetElement instanceof HTMLInputElement)) return;

//     targetElement.checked = true;
//     this.#setLayout('default');
//   };
// }

// if (!customElements.get('results-list')) {
//   customElements.define('results-list', ResultsList);
// }
